import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getDashboardWidgetQuery } from '@src/util/query/dashboardLayout.query';
import { getUniformRequestsQuery } from '@src/util/query/uniform.query';
import { getDutyRequestsQuery } from '@src/util/query/dutyRequest.query';
import { ApproveHolidayModal, RejectHolidayModal } from '@src/components/holiday/holidayRequestActions';
import { MetricTile, PanelCard, useDashboard, useTicks } from './widgetShell';
import { RangeBadge, rangeLabel, rangeVariables } from './widgetRange';

const ukDate = (value) => (value ? dayjs(value).format('DD MMM') : '');
const dates = (from, to) => (ukDate(from) === ukDate(to) ? ukDate(from) : `${ukDate(from)} – ${ukDate(to)}`);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const calendarOf = (employeeId) => `/user/employee-detail/${employeeId}/tab/calendar`;

/**
 * Who's away: employees with approved leave in the period (today by default). A tall widget lists
 * them; any name opens their Calendar tab.
 */
export const OnHolidayWidget = ({ settings, onSettings, size }) => {
  const navigate = useNavigate();
  const { openRows } = useDashboard();
  const { data, loading } = useQuery(getDashboardWidgetQuery, {
    variables: { widget: 'onHoliday', ...rangeVariables(settings) },
    fetchPolicy: 'network-only',
  });
  const response = data?.getDashboardWidget?.response;
  const rows = response?.rows || [];
  const period = rangeLabel(settings).toLowerCase();
  const columns = [
    { key: 'name', label: 'Employee', width: '1.5fr', render: (row) => <span className="dw-link" onClick={() => navigate(calendarOf(row.employeeId))}>{row.name}</span> },
    { key: 'dates', label: 'Away', width: '1.2fr', render: (row) => dates(row.from, row.to) },
    { key: 'days', label: 'Days', width: '0.6fr', render: (row) => row.days },
    { key: 'type', label: 'Leave', width: '1.2fr', render: (row) => row.types?.join(', ') || '-' },
  ];
  const open = () => openRows({ title: `On holiday (${rangeLabel(settings)})`, columns, rows, emptyText: `Nobody is on holiday ${period === 'today' ? 'today' : 'in this period'}.` });
  const badge = <RangeBadge settings={settings} onChange={onSettings} />;

  if (size.h >= 2) {
    return (
      <PanelCard title="On holiday" extra={badge}>
        <div className="dw-count-line"><b data-metric-value>{response ? response.count : '…'}</b> {response?.count === 1 ? 'employee' : 'employees'} away</div>
        <div className="dw-list">
          {rows.length === 0 && !loading && <div className="dw-list-empty">Nobody is on holiday {period === 'today' ? 'today' : 'in this period'}.</div>}
          {rows.map((row) => (
            <div key={row.employeeId} className="dw-list-row" onClick={() => navigate(calendarOf(row.employeeId))}>
              <span className="dw-list-main">{row.name}</span>
              <span className="dw-list-sub">{dates(row.from, row.to)} · {row.types?.join(', ')}</span>
            </div>
          ))}
        </div>
      </PanelCard>
    );
  }
  return (
    <MetricTile
      label="On holiday"
      value={loading && !data ? undefined : (response?.count || 0)}
      loading={loading}
      badge={badge}
      onClick={open}
      sub={rows.length ? rows.slice(0, 2).map((r) => r.name).join(', ') + (rows.length > 2 ? ` +${rows.length - 2} more` : '') : null}
    />
  );
};

/**
 * Leave requests from staff waiting for a decision. Approve or decline right here (the same
 * pop-ups as the employee's Calendar tab); a tall widget lists them.
 */
export const HolidayRequestsWidget = ({ size }) => {
  const navigate = useNavigate();
  const { openRows, updateRows } = useDashboard();
  const [deciding, setDeciding] = useState(null);
  const { data, loading, refetch } = useQuery(getDashboardWidgetQuery, {
    variables: { widget: 'holidayRequests' },
    fetchPolicy: 'network-only',
    pollInterval: 60000,
  });
  useTicks(['holiday'], refetch);
  const response = data?.getDashboardWidget?.response;
  const rows = response?.rows || [];
  const count = response?.count || 0;
  const title = 'Holiday requests';

  const actions = (row) => (
    <span className="dw-actions" onClick={(e) => e.stopPropagation()}>
      <button className="dw-action approve" onClick={() => setDeciding({ kind: 'approve', row })}>Approve</button>
      <button className="dw-action" onClick={() => setDeciding({ kind: 'reject', row })}>Decline</button>
    </span>
  );
  const columns = [
    { key: 'name', label: 'Employee', width: '1.4fr', render: (row) => <span className="dw-link" onClick={() => navigate(calendarOf(row.employeeId))}>{row.name}</span> },
    { key: 'dates', label: 'Dates', width: '1.2fr', render: (row) => dates(row.startDate, row.endDate) },
    { key: 'days', label: 'Days', width: '0.6fr', render: (row) => Math.round((row.days || 0) * 100) / 100 },
    { key: 'type', label: 'Leave', width: '1fr', render: (row) => row.type },
    { key: 'actions', label: '', width: '1.4fr', render: actions },
  ];
  // keep an open list in step after a decision
  useEffect(() => { updateRows?.(title, rows); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = () => openRows({ title, columns, rows, emptyText: 'No leave requests are waiting.' });
  const done = () => { setDeciding(null); refetch(); };

  return (
    <>
      {size.h >= 2 ? (
        <PanelCard title={title} extra={<span className="metric-badge">Waiting</span>}>
          <div className="dw-count-line"><b data-metric-value>{response ? count : '…'}</b> waiting for a decision</div>
          <div className="dw-list">
            {rows.length === 0 && !loading && <div className="dw-list-empty">No leave requests are waiting.</div>}
            {rows.map((row) => (
              <div key={row._id} className="dw-list-row" onClick={() => navigate(calendarOf(row.employeeId))}>
                <span className="dw-list-main">{row.name}</span>
                <span className="dw-list-sub">{dates(row.startDate, row.endDate)} · {row.type}</span>
                {actions(row)}
              </div>
            ))}
          </div>
        </PanelCard>
      ) : (
        <MetricTile label={title} value={loading && !data ? undefined : count} loading={loading} alert={count > 0} badge={<span className="metric-badge">Waiting</span>} onClick={open} hint="View requests" />
      )}
      {deciding?.kind === 'approve' && (
        <ApproveHolidayModal visible close={() => setDeciding(null)} onDone={done} requestId={deciding.row._id} employeeId={deciding.row.employeeId} />
      )}
      {deciding?.kind === 'reject' && (
        <RejectHolidayModal visible close={() => setDeciding(null)} onDone={done} requestId={deciding.row._id} employeeId={deciding.row.employeeId} />
      )}
    </>
  );
};

// Customers' service requests (from wfc-customer) waiting on the office - opens the pop-up to action them
export const ServiceRequestsWidget = () => {
  const { openRequests } = useDashboard();
  const { data, loading, refetch } = useQuery(getDutyRequestsQuery, {
    variables: { input: { page: 1, pageSize: 1, status: 'pending' } },
    fetchPolicy: 'network-only',
  });
  useTicks(['service'], refetch);
  const pending = data?.getDutyRequests?.response?.totalCount || 0;
  return (
    <MetricTile label="Service Requests" value={loading && !data ? undefined : pending} loading={loading} alert={pending > 0} badge={<span className="metric-badge">Pending</span>} onClick={() => openRequests('service')} hint="View requests" />
  );
};

// Officers' uniform requests: waiting for approval, and approved ones still to hand over
export const UniformRequestsWidget = () => {
  const { openRequests } = useDashboard();
  const pendingQuery = useQuery(getUniformRequestsQuery, { variables: { filter: { status: ['pending'], pageSize: 1 } }, fetchPolicy: 'network-only' });
  const approvedQuery = useQuery(getUniformRequestsQuery, { variables: { filter: { status: ['approved'], pageSize: 1 } }, fetchPolicy: 'network-only' });
  useTicks(['uniform'], () => { pendingQuery.refetch(); approvedQuery.refetch(); });
  const pending = pendingQuery.data?.getUniformRequests?.response?.totalCount || 0;
  const toIssue = approvedQuery.data?.getUniformRequests?.response?.totalCount || 0;
  return (
    <MetricTile
      label="Uniform Requests"
      value={pendingQuery.loading && !pendingQuery.data ? undefined : pending}
      loading={pendingQuery.loading}
      alert={pending > 0}
      badge={<span className="metric-badge">Pending approval</span>}
      onClick={() => openRequests('uniform')}
      hint="View requests"
      sub={toIssue > 0 ? `${plural(toIssue, 'approved', 'approved')}, to issue` : null}
    />
  );
};
